import BaseCheckbox from '@components/BaseCheckbox';
import { BaseTextField } from '@components/BaseTextField';
import PopUpBase from '@components/PopUpBase';
import StyledAutocomplete from '@components/styled-autocomplete';
import useStyledAutocomplete from '@components/styled-autocomplete/useStyledAutocomplete';
import { IntervalUnit, PTZSnapShotSetting } from '@EcamModel/model/PTZSnapshotSetting';
import { IPopUp } from '@hooks/usePopUp';
import { Button, FormControlLabel, Stack, Typography } from '@mui/material';
import { useCameraDetailContext } from '@pages/cameras/hook/CameraDetailsProvider';
import { BaseSelect } from '@pages/cameras/list';
import { search } from '@pages/cameras/list/helpers';
import color from '@theme/Colors';
import { useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';

type Props = Omit<IPopUp, 'onConfirm'> & {
    handleUpdateSnapshotSettings: (data: PTZSnapShotSettingFormValues) => Promise<void>;
    ptzSnapShotSetting?: PTZSnapShotSetting;
};

export const intervalUnits: BaseSelect[] = [
    {
        Id: 1,
        Name: 'Seconds',
        Value: IntervalUnit.SECONDS,
    },
    {
        Id: 2,
        Name: 'Minutes',
        Value: IntervalUnit.MINUTES,
    },
    {
        Id: 3,
        Name: 'Hours',
        Value: IntervalUnit.HOURS,
    },
    {
        Id: 4,
        Name: 'Days',
        Value: IntervalUnit.DAYS,
    },
];

export interface PTZSnapShotSettingFormValues {
    TimingSnapshotEnable: boolean;
    IntervalValue: number;
    IntervalUnit: BaseSelect;
}

export default function PopUpEditSnapshotSettings(props: Props) {
    const { open, onClose, handleUpdateSnapshotSettings, ptzSnapShotSetting } = props;

    const { patrolTimeForecast } = useCameraDetailContext();

    const defaultValues: PTZSnapShotSettingFormValues = useMemo(
        () => ({
            TimingSnapshotEnable: ptzSnapShotSetting?.TimingSnapshotEnable === 1 ? true : false,
            IntervalValue: ptzSnapShotSetting?.IntervalValue || 0,
            IntervalUnit:
                intervalUnits.find((unit) => unit.Value === ptzSnapShotSetting?.IntervalUnit) || intervalUnits[0],
        }),
        [ptzSnapShotSetting]
    );

    const {
        control,
        clearErrors,
        reset,
        handleSubmit,
        formState: { isDirty, isValid },
        watch,
    } = useForm<PTZSnapShotSettingFormValues>({
        mode: 'all',
        defaultValues: defaultValues,
    });

    const disabled = !(isValid && isDirty);

    const intervalUnitsAutocomplete = useStyledAutocomplete({
        list: {
            options: intervalUnits,
            isFiltered: (option, searchText) => search(option.Name, searchText),
        },
    });

    useEffect(() => {
        if (!open) return;

        reset(defaultValues);
    }, [open, ptzSnapShotSetting, defaultValues]);

    return (
        <PopUpBase
            open={open}
            onClose={onClose}
            onConfirm={() => {}}
            dialogProps={{
                fullWidth: true,
                sx: {
                    '& .MuiDialog-paper': {
                        borderRadius: '10px',
                        maxWidth: 800,
                    },
                    '& .MuiPaper-root': {
                        overflowY: 'unset',
                    },
                    zIndex: (theme) => theme.zIndex.modal,
                },
            }}
            title={
                <Stack>
                    <Typography variant="h4">Snapshot Settings</Typography>
                </Stack>
            }
            titleProps={{ '&.MuiDialogTitle-root': { paddingTop: '24px' } }}
            subTitleProps={{ sx: { color: 'gray' } }}
            minWidthButton={150}
            fixOverflow
            desc={
                <Stack sx={{ background: '#FAFAFA' }} spacing={2} p={2}>
                    <Typography variant="h6">Snapshot Settings</Typography>

                    <Stack direction="row" spacing={2} alignItems="center">
                        <Controller
                            name="TimingSnapshotEnable"
                            control={control}
                            render={({ field }) => {
                                return (
                                    <FormControlLabel
                                        control={
                                            <BaseCheckbox
                                                {...field}
                                                onChange={(_, checked) => {
                                                    return field.onChange(checked);
                                                }}
                                                checked={!!field.value}
                                                value={field.value}
                                            />
                                        }
                                        label="Enable timing snapshot:"
                                        labelPlacement="start"
                                        sx={{
                                            ml: 0,
                                            '& .MuiFormControlLabel-label': {
                                                marginRight: '8px',
                                            },
                                            '& .MuiFormControlLabel-root': {
                                                flexDirection: 'row-reverse',
                                            },
                                        }}
                                    />
                                );
                            }}
                        />
                    </Stack>

                    <Stack direction="row" spacing={2} alignItems="center">
                        <Typography>Interval:</Typography>
                        <Controller
                            name="IntervalValue"
                            control={control}
                            render={({ field, fieldState: { error } }) => (
                                <BaseTextField
                                    {...field}
                                    value={field.value}
                                    maxLength={10}
                                    onChange={(e) => {
                                        const value = Number(e.target.value);
                                        if (!isNaN(value)) {
                                            field.onChange(value);
                                        }
                                    }}
                                    error={!!error?.message}
                                    helperText={error?.message}
                                    variant="outlined"
                                    size="small"
                                />
                            )}
                        />
                        <Controller
                            name="IntervalUnit"
                            control={control}
                            render={({ field }) => (
                                <StyledAutocomplete
                                    {...intervalUnitsAutocomplete}
                                    getOptionLabel={(o) => o.Name}
                                    label="Interval unit"
                                    value={field.value}
                                    onChange={(o) => {
                                        field.onChange(o);
                                    }}
                                />
                            )}
                        />
                    </Stack>

                    <Typography
                        variant="caption"
                        color={color.grey600}
                        textAlign={'left'}
                        p={1}
                        borderRadius={1}
                        bgcolor={color.white}
                    >
                        Patrol sequence: The estimated patrol time is{' '}
                        <span
                            style={{
                                color: color.textSecondary,
                            }}
                        >
                            {patrolTimeForecast?.TimeForecast}s.
                        </span>
                        <br />
                        The value of{' '}
                        <em
                            style={{
                                color: color.textSecondary,
                            }}
                        >
                            Interval
                        </em>{' '}
                        should be greater than or equal to the value of{' '}
                        <em
                            style={{
                                color: color.textSecondary,
                            }}
                        >
                            Patrol sequence
                        </em>{' '}
                        to avoid errors.
                    </Typography>
                </Stack>
            }
            customActions={
                <Stack direction={'row'} width="100%" justifyContent="space-between">
                    <Button
                        sx={{ minWidth: 130 }}
                        variant="cancel"
                        onClick={() => {
                            props.onClose?.();
                            clearErrors();
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        sx={{ minWidth: 130 }}
                        variant="contained"
                        disabled={disabled}
                        onClick={handleSubmit(async (data) => {
                            await handleUpdateSnapshotSettings(data);
                            props.onClose?.();
                        })}
                    >
                        Save
                    </Button>
                </Stack>
            }
        />
    );
}
